import * as THREE from 'three';
import { getSprites } from './textures';
import { START_X, polylineLength, resampleByArc, type Shot } from './geodesic';
import { hdr } from './bodies';

/** points revealed per second, for both paths, and the spacing between points: 160 per second is 10 Sun-radii a second */
export const REVEAL_PER_SECOND = 160;
const POINT_SPACING = 0.0625;
const MAX_POINTS = 1500;

function flatToXYZ(p: number[]): Float32Array {
  const n = p.length / 2, out = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) { out[3 * i] = p[2 * i]; out[3 * i + 1] = p[2 * i + 1]; out[3 * i + 2] = 0; }
  return out;
}

function pathPoints(flat: number[]): number {
  return Math.min(MAX_POINTS, Math.max(2, Math.round(polylineLength(flat) / POINT_SPACING)));
}

/**
 * Everything to do with the player's ray: the launch handle and its dashed guide, then, once sent,
 * Einstein's path in gold, drawn progressively with a bright head, and Newton's ghost in dashed ice-blue at the same pace.
 */
export class ShotView {
  readonly group = new THREE.Group();
  private handle = new THREE.Group();
  private guide: THREE.Line;
  private guidePos: Float32Array;
  private head: THREE.Sprite;
  private eLine: THREE.Line | null = null;
  private ePoints: THREE.Points | null = null;
  private nLine: THREE.Line | null = null;
  private ePos: Float32Array | null = null;
  private ne = 0;
  private nn = 0;

  private eLineMat = new THREE.LineBasicMaterial({ color: hdr(1.9, 1.35, 0.55), transparent: true, opacity: 0.95, blending: THREE.AdditiveBlending, depthWrite: false });
  private ePointsMat: THREE.PointsMaterial;
  private nLineMat = new THREE.LineDashedMaterial({ color: hdr(0.7, 1.6, 2.4), dashSize: 0.3, gapSize: 0.2, transparent: true, opacity: 1, blending: THREE.AdditiveBlending, depthWrite: false });

  constructor() {
    const { soft, hard } = getSprites();
    // the launch handle, a small white sprite with a halo
    const core = new THREE.Sprite(new THREE.SpriteMaterial({ map: hard, color: hdr(1.8, 1.8, 1.9), transparent: true, blending: THREE.AdditiveBlending, depthWrite: false }));
    core.scale.set(0.42, 0.42, 1);
    const halo = new THREE.Sprite(new THREE.SpriteMaterial({ map: soft, color: hdr(0.9, 1.0, 1.3), opacity: 0.5, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false }));
    halo.scale.set(1.5, 1.5, 1);
    this.handle.add(halo, core);
    this.group.add(this.handle);

    // the dashed guide: where the ray would go if space were flat
    this.guidePos = new Float32Array([START_X, 0, 0, -START_X, 0, 0]);
    const gg = new THREE.BufferGeometry();
    gg.setAttribute('position', new THREE.BufferAttribute(this.guidePos, 3));
    this.guide = new THREE.Line(gg, new THREE.LineDashedMaterial({ color: hdr(0.45, 0.8, 1.0), dashSize: 0.22, gapSize: 0.3, transparent: true, opacity: 0.3, blending: THREE.AdditiveBlending, depthWrite: false }));
    this.guide.computeLineDistances();
    this.guide.frustumCulled = false;
    this.group.add(this.guide);

    this.ePointsMat = new THREE.PointsMaterial({ map: hard, size: 0.2, color: hdr(1.5, 1.05, 0.4), transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, sizeAttenuation: true });
    this.head = new THREE.Sprite(new THREE.SpriteMaterial({ map: hard, color: hdr(3.2, 2.7, 1.8), transparent: true, blending: THREE.AdditiveBlending, depthWrite: false }));
    this.head.scale.set(0.7, 0.7, 1);
    this.head.visible = false;
    this.group.add(this.head);
    this.setHeight(2.2);
  }

  /** Move the launch handle and its guide to the starting height b. Only two floats change; the dash lengths stay valid. */
  setHeight(b: number) {
    this.handle.position.set(START_X, b, 0);
    this.guidePos[1] = b; this.guidePos[4] = b;
    this.guide.geometry.attributes.position.needsUpdate = true;
  }

  /** Build the two paths for this shot and hide them until `reveal` uncovers them. */
  launch(shot: Shot) {
    this.clear();
    const pe = resampleByArc(shot.einstein.points, pathPoints(shot.einstein.points));
    const pn = resampleByArc(shot.newton.points, pathPoints(shot.newton.points));
    this.ne = pe.length / 2; this.nn = pn.length / 2;
    this.ePos = flatToXYZ(pe);
    const eg = new THREE.BufferGeometry();
    eg.setAttribute('position', new THREE.BufferAttribute(this.ePos, 3));
    eg.setDrawRange(0, 0);
    this.eLine = new THREE.Line(eg, this.eLineMat);
    this.ePoints = new THREE.Points(eg, this.ePointsMat);
    this.eLine.frustumCulled = false; this.ePoints.frustumCulled = false;
    const ng = new THREE.BufferGeometry();
    ng.setAttribute('position', new THREE.BufferAttribute(flatToXYZ(pn), 3));
    ng.setDrawRange(0, 0);
    this.nLine = new THREE.Line(ng, this.nLineMat);
    this.nLine.computeLineDistances();
    this.nLine.frustumCulled = false;
    this.group.add(this.eLine, this.ePoints, this.nLine);
    this.head.visible = true;
    this.reveal(0);
  }

  /** How many points the slower path needs before both are fully drawn. */
  get total() { return Math.max(this.ne, this.nn); }
  /** Points in Einstein's path, so the caller can fade the head when it has arrived. */
  get einsteinPoints() { return this.ne; }

  /** Uncover the first k points of each path and put the bright head on Einstein's. */
  reveal(k: number) {
    if (!this.eLine || !this.nLine || !this.ePos) return;
    const ke = Math.max(0, Math.min(this.ne, k)), kn = Math.max(0, Math.min(this.nn, k));
    this.eLine.geometry.setDrawRange(0, ke);
    this.nLine.geometry.setDrawRange(0, kn);
    const j = Math.max(0, Math.min(this.ne - 1, k));
    this.head.position.set(this.ePos[3 * j], this.ePos[3 * j + 1], 0);
    const fade = Math.max(0, 1 - Math.max(0, k - this.ne) / 80);
    const s = 0.7 * fade * (1 + 0.12 * Math.sin(k * 0.5));
    this.head.scale.set(s, s, 1);
    this.head.visible = fade > 0;
  }

  /** Remove the current shot, keeping the handle and guide. */
  clear() {
    for (const o of [this.eLine, this.ePoints, this.nLine]) { if (o) this.group.remove(o); }
    this.eLine?.geometry.dispose(); this.nLine?.geometry.dispose();
    this.eLine = null; this.ePoints = null; this.nLine = null; this.ePos = null;
    this.ne = 0; this.nn = 0;
    this.head.visible = false;
  }
}
